import {
  Box,
  Callout,
  ChevronSmall,
  CollapsibleComponent,
  DebtRepaymentSuggestion,
  DebtRepaymentSuggestionMonth,
  Disclaimer,
  Div,
  H5,
  Icon,
  P,
  displayCentsInDollars,
  lightTheme,
  useMeasuredHeight,
} from '@vgrubs/components';
import { useState } from 'react';
import {
  PLAN_AMOUNT_MAX,
  PLAN_AMOUNT_MIN,
} from '../helpers/repaymentPlanLabels';
import {
  displayWholeDollars,
  formatMonth,
} from '../helpers/repaymentSuggestionLabels';
import {
  BulletList,
  DetailsToggle,
  FigureRow,
} from './RepaymentSuggestion.styled';

type Props = {
  data: DebtRepaymentSuggestion;
  /** The reasoning and risks of the option on show; null = none (option B's AI failed). */
  ai: { reasoning: string; risks: string[] } | null;
};

/**
 * The months as rows of figures, one column per month: three columns fit a
 * phone, seven figures across did not. Whole dollars: context for the plan,
 * and with cents three columns of them broke mid-number on a phone.
 */
const MONTH_ROWS: {
  label: string;
  value: (month: DebtRepaymentSuggestionMonth) => string;
}[] = [
  { label: 'Orders', value: (m) => String(m.virtualBrandOrders) },
  {
    label: 'Payout',
    value: (m) => displayWholeDollars(m.virtualBrandPayout),
  },
  { label: 'Deliveries', value: (m) => String(m.deliveries) },
  {
    label: 'Delivery cost',
    value: (m) => displayWholeDollars(m.deliveryCost),
  },
  {
    label: 'vPay Card in',
    value: (m) => displayWholeDollars(m.balanceInflow),
  },
  {
    label: 'To vDrive first',
    value: (m) => displayWholeDollars(m.balanceToVDrive),
  },
  {
    label: 'Left for a plan',
    value: (m) => displayWholeDollars(m.balanceNet),
  },
];

/**
 * "Up to $2.85 per order: 15% of the $19.00 average payout", with the $4.00
 * cap named when that is what set it. Null limit: nothing to take it from.
 */
const limitLine = (
  limit: number | null,
  average: number | null,
  percent: number,
  what: { unit: string; average: string; none: string }
) => {
  if (limit == null || average == null)
    return `Not available per ${what.unit}: ${what.none} in these 3 months.`;
  const capped =
    limit === PLAN_AMOUNT_MAX && (average * percent) / 100 > PLAN_AMOUNT_MAX;
  return `Up to ${displayCentsInDollars(limit)} per ${
    what.unit
  }: ${percent}% of the ${displayCentsInDollars(average)} ${what.average}${
    capped ? `, capped at ${displayCentsInDollars(PLAN_AMOUNT_MAX)}` : ''
  }.`;
};

/**
 * What the suggestion stands on, closed by default: the AI's reasoning and
 * risks, the three months it looked at, and the limits its amounts were kept
 * within.
 */
export const SuggestionDetails = ({ data, ai }: Props) => {
  const [open, setOpen] = useState(false);
  const { ref, height } = useMeasuredHeight<HTMLDivElement>();
  const { months, averages, limits } = data;

  return (
    // Box's own min-height left empty space under the closed header.
    <Box $minHeight="auto" $minHeightSM="auto">
      <DetailsToggle onClick={() => setOpen((value) => !value)}>
        <H5 $m="0">Details</H5>
        <Icon $rotate={open ? '180deg' : '0'}>
          <ChevronSmall />
        </Icon>
      </DetailsToggle>

      <CollapsibleComponent
        expanded={open}
        elementHeight={height}
        marginTop="12px"
      >
        <Div ref={ref} $flex $column $gap="16px">
          {ai?.reasoning && (
            <div>
              <P $m="0 0 4px" $bold>
                Why these amounts
              </P>
              <P $m="0">{ai.reasoning}</P>
            </div>
          )}

          {ai && ai.risks.length > 0 && (
            <div>
              <P $m="0 0 8px" $bold>
                Watch out for
              </P>
              <Callout tone="warning">
                <BulletList>
                  {ai.risks.map((risk) => (
                    <li key={risk}>{risk}</li>
                  ))}
                </BulletList>
              </Callout>
            </div>
          )}

          <div>
            <P $m="0" $bold>
              Last 3 months
            </P>
            <Disclaimer $m="0 0 4px" $color={lightTheme.lightGray}>
              Virtual-brand orders, deliveries paid from the location's own
              vDrive balance, and the vGrubs balance (vPay Card).
            </Disclaimer>
            <FigureRow $columns={months.length}>
              <span />
              {months.map((month) => (
                <Disclaimer
                  key={month.month}
                  $m="0"
                  $color={lightTheme.lightGray}
                >
                  {formatMonth(month.month)}
                </Disclaimer>
              ))}
            </FigureRow>
            {MONTH_ROWS.map((row) => (
              <FigureRow key={row.label} $columns={months.length}>
                <P $m="0">{row.label}</P>
                {months.map((month) => (
                  <P key={month.month} $m="0">
                    {row.value(month)}
                  </P>
                ))}
              </FigureRow>
            ))}
          </div>

          <div>
            <P $m="0 0 4px" $bold>
              Limits
            </P>
            <Disclaimer $m="0" $color={lightTheme.lightGray}>
              About {averages.ordersPerMonth} orders and{' '}
              {averages.deliveriesPerMonth} deliveries a month,{' '}
              {displayCentsInDollars(averages.balanceNetPerMonth)} a month left
              on the vPay Card.
            </Disclaimer>
            <BulletList style={{ marginTop: 8 }}>
              <li>
                {limitLine(
                  limits.maxPerOrderAmount,
                  averages.avgOrderPayout,
                  15,
                  {
                    unit: 'order',
                    average: 'average payout',
                    none: 'no orders',
                  }
                )}
              </li>
              <li>
                {limitLine(
                  limits.maxPerDeliveryAmount,
                  averages.avgDeliveryCost,
                  20,
                  {
                    unit: 'delivery',
                    average: 'average delivery cost',
                    none: 'no deliveries',
                  }
                )}
              </li>
              {/* Undefined on a backend without option B: no line then. */}
              {limits.maxDailyBalanceLimit !== undefined && (
                <li>
                  {limits.maxDailyBalanceLimit == null
                    ? 'Not available a day from the vPay Card: nothing lands on it in these 3 months.'
                    : `Up to ${displayCentsInDollars(
                        limits.maxDailyBalanceLimit
                      )} a day from the vPay Card: 10% of what lands on it on an average day.`}
                </li>
              )}
            </BulletList>
            <Disclaimer $m="8px 0 0" $color={lightTheme.lightGray}>
              A recommendation only: a plan itself takes any amount from{' '}
              {displayCentsInDollars(PLAN_AMOUNT_MIN)} to{' '}
              {displayCentsInDollars(PLAN_AMOUNT_MAX)} per order or delivery,
              and any daily limit.
            </Disclaimer>
          </div>
        </Div>
      </CollapsibleComponent>
    </Box>
  );
};
